import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createGroupUrl } from '../../../hooks/useNavigation';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';

interface GroupCardProps {
  group: {
    id: string;
    sequential_id: number;
    name: string;
    description: string;
    cover_image: string;
    category: string;
    privacy: string;
    member_count: number;
  };
  viewType: 'grid' | 'list';
  isMember?: boolean;
  onMembershipChange?: (newIsMember: boolean) => void;
}

const GroupCard = ({ group, viewType, isMember: propIsMember = false, onMembershipChange }: GroupCardProps) => {
  const { user } = useAuth();
  const [isMember, setIsMember] = useState(propIsMember);
  const [memberCount, setMemberCount] = useState(group.member_count);
  const [isLoading, setIsLoading] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  // Sync with prop changes
  useEffect(() => {
    setIsMember(propIsMember);
  }, [propIsMember]);

  useEffect(() => {
    setMemberCount(group.member_count);
  }, [group.member_count]);

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Entertainment': 'bg-purple-100 text-purple-700',
      'Production': 'bg-blue-100 text-blue-700',
      'Genre': 'bg-pink-100 text-pink-700',
      'Regional': 'bg-green-100 text-green-700',
      'Professional': 'bg-orange-100 text-orange-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const handleJoinLeave = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user || isLoading) return;

    setIsLoading(true);
    try {
      if (isMember) {
        // Leave group
        const { error } = await supabase
          .from('group_members')
          .delete()
          .eq('group_id', group.id)
          .eq('user_id', user.id);

        if (error) throw error;

        await supabase
          .from('groups')
          .update({ member_count: Math.max(0, memberCount - 1) })
          .eq('id', group.id);

        setIsMember(false);
        setMemberCount(prev => Math.max(0, prev - 1));
        onMembershipChange?.(false);
      } else {
        // Join group
        const { error } = await supabase
          .from('group_members')
          .insert({
            group_id: group.id,
            user_id: user.id,
            role: 'member',
            badge: null,
            level: 1,
          });

        if (error) throw error;

        await supabase
          .from('groups')
          .update({ member_count: memberCount + 1 })
          .eq('id', group.id);

        setIsMember(true);
        setMemberCount(prev => prev + 1);
        onMembershipChange?.(true);
      }
    } catch (error) {
      console.error('Error joining/leaving group:', error);
    } finally {
      setIsLoading(false);
      setIsHovering(false);
    }
  };

  const groupUrl = createGroupUrl(group.sequential_id, group.name);

  const renderJoinButton = (fullWidth: boolean) => {
    if (!user) return null;

    if (isMember) {
      return (
        <button
          onClick={handleJoinLeave}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          disabled={isLoading}
          className={`${fullWidth ? 'w-full' : ''} py-2 px-4 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
            isHovering
              ? 'bg-red-50 text-red-600 border border-red-200'
              : 'bg-gray-100 text-gray-500 border border-gray-200'
          } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Leaving...
            </span>
          ) : isHovering ? (
            <span className="flex items-center justify-center gap-1">
              <i className="ri-logout-box-line text-xs"></i>
              Leave
            </span>
          ) : (
            <span className="flex items-center justify-center gap-1">
              <i className="ri-check-line text-xs"></i>
              Joined
            </span>
          )}
        </button>
      );
    }

    return (
      <button
        onClick={handleJoinLeave}
        disabled={isLoading}
        className={`${fullWidth ? 'w-full' : ''} py-2 px-4 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap bg-teal-500 text-white hover:bg-teal-600 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin"></i>
            Joining...
          </span>
        ) : (
          'Join Group'
        )}
      </button>
    );
  };

  if (viewType === 'list') {
    return (
      <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all overflow-hidden border border-gray-100">
        <div className="flex items-center gap-4 p-4">
          <Link to={groupUrl} className="flex-shrink-0">
            <img
              src={group.cover_image}
              alt={group.name}
              className="w-24 h-24 rounded-lg object-cover"
            />
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Link to={groupUrl} className="text-lg font-semibold text-gray-900 hover:text-teal-600 transition-colors truncate">
                {group.name}
              </Link>
              {group.privacy === 'private' && (
                <i className="ri-lock-line text-gray-400 text-sm flex-shrink-0"></i>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{group.description}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(group.category)}`}>
                  {group.category}
                </span>
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <i className="ri-group-line"></i>
                  <span className="font-medium">{memberCount.toLocaleString()}</span>
                  <span className="text-gray-500">members</span>
                </div>
              </div>
              {renderJoinButton(false)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all overflow-hidden border border-gray-100">
      <Link to={groupUrl} className="block">
        <div className="relative h-48 overflow-hidden">
          <img
            src={group.cover_image}
            alt={group.name}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute top-3 right-3">
            {group.privacy === 'private' && (
              <div className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
                <i className="ri-lock-line text-gray-700 text-sm"></i>
                <span className="text-xs font-medium text-gray-700">Private</span>
              </div>
            )}
          </div>
        </div>
      </Link>
      <div className="p-4">
        <Link to={groupUrl}>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-teal-600 transition-colors line-clamp-1">
            {group.name}
          </h3>
        </Link>
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{group.description}</p>
        <div className="flex items-center justify-between mb-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(group.category)}`}>
            {group.category}
          </span>
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <i className="ri-group-line"></i>
            <span className="font-medium">{memberCount.toLocaleString()}</span>
          </div>
        </div>
        {renderJoinButton(true)}
      </div>
    </div>
  );
};

export default GroupCard;
